import React, { useMemo, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiDownload, FiFolder, FiX } from 'react-icons/fi';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import S from './Widget.module.scss';

const getRapportFields = (rapport, index) => ({
  key: rapport.report_id || rapport.rapport_id || index,
  name:
    rapport.template_name ||
    rapport.report_name ||
    rapport.titre ||
    rapport.title ||
    rapport.name ||
    checkMot('rapport'),
  date: rapport.generate_report_time || rapport.date || rapport.timestamp || null,
  downloadPath:
    rapport.path_report || rapport.report_path || rapport.file_path || null,
});

const VISIBLE = 2;

const WidgetRapports = ({ rapports = [], loading = false }) => {
  const [openProject, setOpenProject] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Grouper les rapports par project_name
  const projectGroups = useMemo(() => {
    const groups = {};
    rapports.forEach((rapport, index) => {
      const projectName = rapport.project_name || checkMot('autre');
      if (!groups[projectName]) groups[projectName] = [];
      groups[projectName].push({ ...rapport, _index: index });
    });
    return Object.entries(groups); // [[projectName, [rapports]], ...]
  }, [rapports]);

  const hasRapports = projectGroups.length > 0;
  const canPrev = currentIndex > 0;
  const canNext = currentIndex + VISIBLE < projectGroups.length;
  const visibleGroups = projectGroups.slice(currentIndex, currentIndex + VISIBLE);
  const popupRapports = openProject
    ? projectGroups.find(([name]) => name === openProject)?.[1] ?? []
    : [];

  return (
    <>
      <div className={[S.widget, S.widgetMedium, S.widgetSimple].join(' ')}>
        <div className={S.simpleHeader}>
          <h3 className={S.simpleTitle}>{checkMot('mes_rapports')}</h3>
        </div>
        <div className={S.simpleContent}>
          {loading ? (
            <div className={S.widgetLoadingState}>
              <div className={S.widgetLoadingSpinner} />
              <span className={S.widgetLoadingText}>{checkMot('chargement_en_cours')}</span>
            </div>
          ) : hasRapports ? (
            <div className={S.rapportCarousel}>
              <button
                type="button"
                className={S.carouselArrow}
                onClick={() => setCurrentIndex(i => i - 1)}
                disabled={!canPrev}
                aria-label="Précédent"
              >
                <FiChevronLeft size={18} />
              </button>
              <div className={S.carouselTrack}>
                {visibleGroups.map(([projectName, items]) => (
                  <button
                    key={projectName}
                    type="button"
                    className={S.rapportFolderCard}
                    onClick={() => setOpenProject(projectName)}
                    title={projectName}
                  >
                    <span className={S.rapportFolderCardIcon}>
                      <FiFolder size={32} />
                      <span className={S.rapportFolderCardCount}>{items.length}</span>
                    </span>
                    <span className={S.rapportFolderCardName}>{projectName}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                className={S.carouselArrow}
                onClick={() => setCurrentIndex(i => i + 1)}
                disabled={!canNext}
                aria-label="Suivant"
              >
                <FiChevronRight size={18} />
              </button>
            </div>
          ) : (
            <div className={S.emptyStateSimple}>
              <span className={S.placeholder}>{checkMot('aucun_rapport')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Popup rendu hors du widget pour éviter overflow:hidden */}
      {openProject && (
        <div className={S.rapportPopupOverlay} onClick={() => setOpenProject(null)}>
          <div
            className={S.rapportPopup}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={S.rapportPopupHeader}>
              <span className={S.rapportPopupTitle}>
                <FiFolder size={18} />
                {openProject}
              </span>
              <button
                type="button"
                className={S.rapportPopupClose}
                onClick={() => setOpenProject(null)}
                aria-label="Fermer"
              >
                <FiX size={18} />
              </button>
            </div>
            <ul className={S.rapportPopupList}>
              {popupRapports.map((rapport, index) => {
                const { key, name, date, downloadPath } = getRapportFields(
                  rapport,
                  index
                );
                const dateLabel = date
                  ? new Date(date * 1000).toLocaleDateString()
                  : '';
                return (
                  <li key={key} className={S.rapportPopupItem}>
                    <span className={S.rapportPopupName} title={name}>
                      {name}
                    </span>
                    {dateLabel && (
                      <span className={S.rapportPopupDate}>{dateLabel}</span>
                    )}
                    {downloadPath ? (
                      <a
                        href={downloadPath}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className={S.rapportDownloadBtn}
                        title={checkMot('telecharger')}
                      >
                        <FiDownload size={15} />
                        {checkMot('telecharger')}
                      </a>
                    ) : (
                      <span className={S.rapportDownloadBtnDisabled}>
                        <FiDownload size={15} />
                        {checkMot('telecharger')}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default WidgetRapports;
